import {
  BKI_BASELINE,
  BKI_CITIES,
  BKI_COMMERCIAL_BASELINE,
  BKI_DEFAULTS,
  type BkiCity,
  type BkiCommercialRates,
  type BkiKgRates,
  type BkiSizeBracket,
  type BkiStandard,
} from "src/service/cost/data/bki";

// Settings-agnostic BKI lookup. Takes a city + (size bracket, standard of
// fit-out) and returns city-adjusted €/m² rates per DIN 276 cost group.
//
// The function is intentionally decoupled from any settings object — it
// takes its inputs as plain parameters so it can be called from an admin
// screen's inline options OR (later, if/when productionised) from a
// per-project CostSettings entry without code changes.

export type BkiOptions = {
  city: BkiCity;
  sizeBracket?: BkiSizeBracket;
  standard?: BkiStandard;
};

export type BkiResolvedRates = BkiKgRates & {
  /** the city's regional multiplier */
  factor: number;
  /** baseline before applying factor, for traceability */
  baseline: BkiKgRates;
  /**
   * City-adjusted commercial rates for the KGs that differ from residential
   * (300/400). Use these for the commercial portion of a mixed-use building;
   * every other KG uses the residential rates above.
   */
  commercial: BkiCommercialRates;
  /** echoed back so callers know what was used (defaults included) */
  resolved: Required<BkiOptions>;
};

export function applyBki(opts: BkiOptions): BkiResolvedRates {
  const city = opts.city;
  const sizeBracket = opts.sizeBracket ?? BKI_DEFAULTS.sizeBracket;
  const standard = opts.standard ?? BKI_DEFAULTS.standard;

  const baseline = BKI_BASELINE[sizeBracket][standard];
  const factor = BKI_CITIES[city];

  return {
    kg200: baseline.kg200 * factor,
    kg300: baseline.kg300 * factor,
    kg400: baseline.kg400 * factor,
    kg500: baseline.kg500 * factor,
    kg600: baseline.kg600 * factor,
    factor,
    baseline,
    commercial: {
      kg300: BKI_COMMERCIAL_BASELINE.kg300 * factor,
      kg400: BKI_COMMERCIAL_BASELINE.kg400 * factor,
    },
    resolved: { city, sizeBracket, standard },
  };
}

/**
 * Multiply each KG rate by a building's GIA to get absolute €. Handy for
 * the cost-engine-compare screen which works in totals, not per-m² rates.
 */
export function bkiTotals(rates: BkiKgRates, giaM2: number) {
  return {
    kg200: rates.kg200 * giaM2,
    kg300: rates.kg300 * giaM2,
    kg400: rates.kg400 * giaM2,
    kg500: rates.kg500 * giaM2,
    kg600: rates.kg600 * giaM2,
    subtotal: (rates.kg200 + rates.kg300 + rates.kg400 + rates.kg500 + rates.kg600) * giaM2,
  };
}
